import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { User } from "firebase/auth";
import type { AccountInput, AccountState } from "@puntopy/contracts";
import { completeRedirectSignIn, firebaseAuth, firebaseConfigured, firebaseRequired, onAuthStateChanged, signOutFromFirebase } from "./firebase.js";
import { getJson, putJson } from "./api-client.js";

interface Session {
  user: User | null;
  state: AccountState | null;
  ready: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  saveProfile: (input: AccountInput) => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function AccountProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [state, setState] = useState<AccountState | null>(null);
  const [ready, setReady] = useState(!firebaseConfigured && !firebaseRequired);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const revision = useRef(0);
  const activeUid = useRef<string | null>(null);
  const refresh = useCallback(async () => {
    const version = ++revision.current;
    if (!firebaseAuth?.currentUser) { setState(null); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const next = await getJson<AccountState>("/v1/account");
      if (version === revision.current) setState(next);
    } catch (failure) {
      if (version === revision.current) setError(failure instanceof Error ? failure.message : "No se pudo cargar tu cuenta.");
    } finally { if (version === revision.current) setLoading(false); }
  }, []);

  useEffect(() => {
    if (firebaseAuth === null) { setReady(true); return; }
    let active = true;
    let authChecked = false;
    let redirectChecked = false;
    const finish = () => { if (active && authChecked && redirectChecked) { window.clearTimeout(timeout); setReady(true); } };
    const timeout = window.setTimeout(() => {
      if (!active) return;
      setError("La conexión de acceso tardó demasiado. Podés reintentar o seguir explorando sin iniciar sesión.");
      setReady(true);
    }, 12_000);
    const unsubscribe = onAuthStateChanged(firebaseAuth, (next) => {
      if (!active) return;
      if (activeUid.current !== next?.uid) { setState(null); setError(null); }
      activeUid.current = next?.uid ?? null;
      setUser(next);
      authChecked = true;
      if (next === null) { revision.current++; setState(null); setLoading(false); }
      else void refresh();
      finish();
    });
    void completeRedirectSignIn().catch((failure) => {
      if (active) setError(failure instanceof Error ? failure.message : "No se pudo completar el acceso con Google.");
    }).finally(() => { redirectChecked = true; finish(); });
    return () => { active = false; revision.current++; unsubscribe(); window.clearTimeout(timeout); };
  }, [refresh]);

  const saveProfile = async (input: AccountInput) => {
    const savingUid = firebaseAuth?.currentUser?.uid;
    ++revision.current;
    setLoading(true);
    try {
      const next = await putJson<AccountState>("/v1/account", input);
      if (!savingUid || firebaseAuth?.currentUser?.uid !== savingUid) return;
      ++revision.current;
      setState(next);
      setError(null);
    } finally { if (firebaseAuth?.currentUser?.uid === savingUid) setLoading(false); }
  };
  const signOut = async () => {
    await signOutFromFirebase();
    revision.current++;
    setUser(null); setState(null); setError(null); setLoading(false);
  };
  return <SessionContext.Provider value={{ user, state, ready, loading, error, refresh, saveProfile, signOut }}>{children}</SessionContext.Provider>;
}

export function useAccount(): Session {
  const session = useContext(SessionContext);
  if (session === null) throw new Error("La sesión de PuntoPY no se pudo inicializar.");
  return session;
}
